import { Reply, Trash2 } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import type { CommentDto, UserOrganizationRole } from "@blinkbooks/types";
import { canManageTeam } from "@/lib/permissions";

interface CommentThreadProps {
  comments: CommentDto[];
  currentUserId?: string;
  role?: UserOrganizationRole | null;
  onReply: (comment: CommentDto) => void;
  onDelete: (id: string) => void;
}

function getInitials(name: string): string {
  return name.split(" ").map((p) => p[0] ?? "").join("").slice(0, 2).toUpperCase();
}

export function CommentThread({ comments, currentUserId, role, onReply, onDelete }: CommentThreadProps) {
  if (comments.length === 0) {
    return <p className="text-sm text-muted-foreground italic py-4 text-center">No comments yet. Start the conversation below.</p>;
  }

  const byId = new Map(comments.map((c) => [c.id, c]));
  const canModerate = canManageTeam(role);

  return (
    <div className="space-y-3">
      {comments.map((comment) => {
        const parent = comment.parentCommentId ? byId.get(comment.parentCommentId) : undefined;
        const canDelete = comment.authorUserId === currentUserId || canModerate;

        return (
          <div key={comment.id} className="flex items-start gap-2.5">
            <div className="flex-shrink-0 h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-semibold text-muted-foreground">
              {getInitials(comment.authorName)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">{comment.authorName}</span>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(parseISO(comment.createdAt), { addSuffix: true })}
                </span>
              </div>
              {parent && (
                <p className="text-xs text-muted-foreground/70 italic truncate">↳ replying to {parent.authorName}: "{parent.text}"</p>
              )}
              <p className="text-sm text-foreground/90 mt-0.5 whitespace-pre-wrap break-words">{comment.text}</p>
              <div className="flex items-center gap-3 mt-1">
                <button
                  onClick={() => onReply(comment)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Reply className="h-3 w-3" /> Reply
                </button>
                {canDelete && (
                  <button
                    onClick={() => onDelete(comment.id)}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
